import { getGetWorkspaceInvitationsApiV1QueryKey } from "@/api/__generated__/api.schema";
import { useCreateInvitationApiV1 } from "@/api/__generated__/api.schema";
import { WorkspaceMemberRole } from "@/api/__generated__/models";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useToastMessage } from "@backpac/monica";
import {
  Button,
  DropdownTriggerButton,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Suspense, useCallback } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { WorkspaceManagedChannelSelect } from "./workspace-managed-channel-select";

const inviteFormSchema = z
  .object({
    role: z.enum([WorkspaceMemberRole.ADMIN, WorkspaceMemberRole.MEMBER]),
    selectedChannels: z.array(z.string()).optional(),
    emails: z
      .array(
        z.object({
          value: z.string().email("Please enter a valid email address"),
        }),
      )
      .min(1, "At least one email is required"),
  })
  .superRefine((data, ctx) => {
    if (data.role === WorkspaceMemberRole.MEMBER) {
      if (!data.selectedChannels || data.selectedChannels.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "At least one channel must be selected for Members",
          path: ["selectedChannels"],
        });
      }
    }
  });

export type InviteFormValues = z.infer<typeof inviteFormSchema>;

interface InviteToWorkspaceFormProps {
  defaultValues?: Partial<InviteFormValues>;
  onClose: () => void;
}

export function InviteToWorkspaceForm({ defaultValues, onClose }: InviteToWorkspaceFormProps) {
  const queryClient = useQueryClient();
  const { info, warning } = useToastMessage();
  const workspaceId = useWorkspaceId();
  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      role: defaultValues?.role ?? WorkspaceMemberRole.MEMBER,
      selectedChannels: defaultValues?.selectedChannels || [],
      emails: defaultValues?.emails || [{ value: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "emails",
  });

  const { mutateAsync: inviteMember } = useCreateInvitationApiV1({
    mutation: {
      onSuccess: async () => {
        info("Invitation sent successfully");
      },
      onError: () => {
        warning("Failed to invite member");
      },
    },
  });
  const onSubmit = useCallback(
    async (formData: InviteFormValues) => {
      await Promise.all(
        formData.emails.map((email) => {
          return inviteMember({
            workspaceId,
            data: {
              email: email.value,
              role: formData.role,
              channelIds: formData.role === "MEMBER" ? formData.selectedChannels : undefined,
            },
          });
        }),
      );
      await queryClient.invalidateQueries({ queryKey: getGetWorkspaceInvitationsApiV1QueryKey(workspaceId) });

      onClose();
    },
    [onClose, workspaceId, inviteMember, queryClient],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>Select role</FormLabel>
                <RadioGroup
                  variant="secondary"
                  value={field.value}
                  onValueChange={field.onChange}
                  defaultValue={WorkspaceMemberRole.MEMBER}
                >
                  <RadioGroupItem value={WorkspaceMemberRole.ADMIN}>Admin</RadioGroupItem>
                  <RadioGroupItem value={WorkspaceMemberRole.MEMBER}>Member</RadioGroupItem>
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="selectedChannels"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>Manage channels</FormLabel>
                <Suspense
                  fallback={
                    <DropdownTriggerButton
                      size="md"
                      disabled={true}
                      className="w-full font-normal text-xs bg-backgroundLight"
                    >
                      Search channel
                    </DropdownTriggerButton>
                  }
                >
                  <FormControl>
                    {form.watch("role") === WorkspaceMemberRole.MEMBER ? (
                      <WorkspaceManagedChannelSelect defaultValues={field.value} onChange={field.onChange} />
                    ) : (
                      <DropdownTriggerButton
                        size="md"
                        disabled={true}
                        className="w-full font-normal text-xs !bg-backgroundLight"
                      >
                        All
                      </DropdownTriggerButton>
                    )}
                  </FormControl>
                </Suspense>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <FormLabel>Invite by email</FormLabel>
            {fields.map((field, index) => (
              <div key={field.id} className="relative">
                <FormField
                  control={form.control}
                  name={`emails.${index}.value`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="relative">
                          <Input {...field} placeholder="Email" className="pr-8" />
                          {fields.length > 1 && (
                            <button
                              type="button"
                              className="absolute flex right-4 top-1/2 -translate-y-1/2 text-textMuted"
                              onClick={() => remove(index)}
                              aria-label="Remove email"
                            >
                              <i className="inline-block i-bp-close size-3" />
                            </button>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              className="p-0"
              onClick={() => append({ value: "" })}
              leftAddon={<i className="inline-block i-bp-plus size-3" />}
            >
              Add more
            </Button>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button type="button" variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!form.formState.isDirty || !form.formState.isValid}
            loading={form.formState.isSubmitting}
          >
            Send invite
          </Button>
        </div>
      </form>
    </Form>
  );
}
